import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  switchMap, map, catchError, withLatestFrom, tap, debounceTime, throttleTime, filter
} from 'rxjs/operators';
import { from, of } from 'rxjs';
import { FrameActions } from '@store/actions/frame.actions';
import { AppState } from '@store/app.state';
import { selectAllFrames, selectCurrentFrameId, selectFrameState, selectLayers, selectCels, selectActiveLayerId } from '@store/selectors/frame.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { PersistenceService } from '@services/persistence.service';
import { CollaborationService } from '@services/collaboration.service';

@Injectable()
export class FrameEffects {
  private actions$ = inject(Actions);
  private store    = inject(Store<AppState>);

  constructor(
    private persistenceService: PersistenceService,
    private collabSvc: CollaborationService
  ) {}

  // ── Load frames ─────────────────────────────────────────────────────────────
  loadFrames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FrameActions.loadFrames),
      switchMap(() =>
        from(this.persistenceService.loadFrames()).pipe(
          map(result => {
            const migrated = this.persistenceService.migrateFrameData(result);
            return FrameActions.loadFramesSuccess({
              frames:        migrated.frames,
              layers:        migrated.layers,
              cels:          migrated.cels,
              activeLayerId: migrated.activeLayerId,
              currentFrameId: migrated.currentFrameId ?? null,
              past:   migrated.past   ?? [],
              future: migrated.future ?? [],
            });
          }),
          catchError(error => of(FrameActions.loadFramesFailure({ error })))
        )
      )
    )
  );

  // ── Auto-save (debounced) ────────────────────────────────────────────────────
  saveFrames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FrameActions.paintPixel,     FrameActions.paintPixels,
        FrameActions.fillPixels,     FrameActions.fillPixelsSymmetry,
        FrameActions.clearFrame,     FrameActions.clearCel,
        FrameActions.addBlankFrame,
        FrameActions.duplicateFrame, FrameActions.deleteFrame,
        FrameActions.moveFrame,      FrameActions.appendFrames,
        FrameActions.setFrames,      FrameActions.replaceFramePixels,
        FrameActions.applySelectionPaste, FrameActions.applySelectionFill,
        FrameActions.cutSelection,   FrameActions.applySelectionTransform,
        FrameActions.addLayer,       FrameActions.deleteLayer,
        FrameActions.renameLayer,    FrameActions.mergeLayersDown,
        FrameActions.moveLayerUp,    FrameActions.moveLayerDown,
        FrameActions.setLayerOpacity, FrameActions.setLayerBlend,
        FrameActions.setLayerTint,   FrameActions.setLayerHold,
        FrameActions.toggleLayerVisible, FrameActions.toggleLayerLocked,
        FrameActions.toggleLayerContinuous,
        FrameActions.resizeCanvas,
        FrameActions.undo,           FrameActions.redo,
        FrameActions.undoCollab,     FrameActions.redoCollab,
        FrameActions.saveFrames,
      ),
      debounceTime(500),
      withLatestFrom(
        this.store.select(selectAllFrames),
        this.store.select(selectCurrentFrameId),
        this.store.select(selectFrameState),
        this.store.select(selectSettings),
        this.store.select(selectLayers),
        this.store.select(selectCels),
        this.store.select(selectActiveLayerId),
      ),
      tap(([_, frames, currentFrameId, frameState, settings, layers, cels, activeLayerId]) => {
        void this.persistenceService.saveFrames({
          frames, layers, cels, activeLayerId,
          currentFrameId,
          past:   frameState.past,
          future: frameState.future,
        });
        const projectId = settings.currentProjectId;
        if (projectId) {
          void this.persistenceService.saveProject(
            projectId, settings.projectName ?? 'Untitled Project',
            frames, currentFrameId,
            layers, cels, activeLayerId,
            frameState.past, frameState.future
          );
        }
      })
    ),
    { dispatch: false }
  );

  // ── Drawing broadcast (throttled 50ms) ──────────────────────────────────────
  collabBroadcastDraw$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FrameActions.paintPixels, FrameActions.paintPixel),
      filter(() => this.collabSvc.canBroadcastCanvasSync() && this.collabSvc.can('draw')),
      throttleTime(50, undefined, { leading: true, trailing: true }),
      withLatestFrom(
        this.store.select(selectAllFrames),
        this.store.select(selectCels),
        this.store.select(selectActiveLayerId),
      ),
      tap(([action, allFrames, cels, activeLayerId]) => {
        const frameId = (action as any).frameId as string | undefined;
        if (!frameId || !activeLayerId) return;
        const frame = allFrames.find(f => f.id === frameId);
        if (!frame) return;
        const h = frame.pixels.length, w = frame.pixels[0]?.length ?? 0;
        if (w > 0 && h > 0) {
          // Broadcast ONLY the cel — NOT a composite frame.
          // If we send a composite here, the receiving peer's syncRemoteFrame overwrites
          // frame.pixels with the sender's partial composite (only the sender's layer),
          // wiping all other layers from the peer's display.
          // Peers recomposite locally via syncRemoteCels → correct display from all known cels.
          const celKey = `${frameId}|${activeLayerId}`;
          const celPixels = cels[celKey];
          if (celPixels) this.collabSvc.broadcastCel(frameId, activeLayerId, celPixels, w, h);
        }
      })
    ),
    { dispatch: false }
  );

  // ── Discrete action broadcast (immediate) ───────────────────────────────────
  collabBroadcastDiscrete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FrameActions.fillPixels,         FrameActions.fillPixelsSymmetry,
        FrameActions.replaceFramePixels, FrameActions.replaceFramePixelsLive,
        FrameActions.applySelectionPaste, FrameActions.applySelectionFill, FrameActions.applySelectionTransform,
        FrameActions.cutSelection,       FrameActions.clearFrame,          FrameActions.clearCel,
        // Structural / multi-frame changes – broadcast everything atomically
        FrameActions.resizeCanvas,
        // Note: undo/redo are intentionally excluded here.
        // In collab mode, undoCollab/redoCollab are dispatched instead (targeted cel broadcast only).
        // In solo mode, undo/redo are fine locally and don't need to broadcast.
      ),
      filter(() => this.collabSvc.canBroadcastCanvasSync() && this.collabSvc.can('draw')),
      withLatestFrom(
        this.store.select(selectAllFrames),
        this.store.select(selectSettings),
        this.store.select(selectLayers),
        this.store.select(selectActiveLayerId),
        this.store.select(selectCels),
      ),
      tap(([action, allFrames, settings, layers, activeLayerId, cels]) => {
        const t = action.type;
        const w = settings.Resolution.width, h = settings.Resolution.height;
        const isFullSync =
          t === FrameActions.resizeCanvas.type;

        if (isFullSync) {
          // Broadcast all frame composites so peers see the correct visual result.
          const framesData = allFrames.map(f => ({
            id: f.id, pixels: f.pixels,
            width:  f.pixels[0]?.length ?? w,
            height: f.pixels.length    ?? h,
          }));
          this.collabSvc.broadcastAllFramesAtomic(framesData);
          this.collabSvc.purgeOrphanedYFrames(allFrames.map(f => f.id));
          // Re-sync layer structure so peers have consistent layers after undo/redo/resize.
          this.collabSvc.broadcastLayers(layers, activeLayerId);
          // Re-sync ALL cels so peers recomposite to the correct post-action state.
          // Without this, peers have stale cel data after undo/redo and produce wrong composites.
          this.collabSvc.broadcastAllCels(cels, w, h);
          return;
        }

        const frameId = (action as any).frameId as string | undefined;
        if (!frameId) return;
        const frame = allFrames.find(f => f.id === frameId);
        if (!frame) return;
        const fw = frame.pixels[0]?.length ?? w, fh = frame.pixels.length ?? h;
        if (fw > 0 && fh > 0) {
          // Broadcast the composite so peers see the result immediately.
          this.collabSvc.broadcastFrame(frameId, frame.pixels, fw, fh);
          // Broadcast ALL layer cels for this frame — not just the active layer.
          //
          // This is defensive and critical: syncRemoteCels recompositees the frame
          // from the peer's full local cel state.  If ANY layer's cel is missing or
          // stale on a peer (e.g. after frame duplication, clearFrame, clearCel, or a
          // missed initial sync), the recomposite drops that layer visually.  By
          // broadcasting every layer's cel here, we guarantee the peer always has
          // complete data and the recomposite is always correct.
          //
          // If a cel is absent (e.g. clearFrame cleared it, or clearCel removed it),
          // we call broadcastCelClear so peers also remove the stale yCels entry.
          for (const layer of layers) {
            const key = `${frameId}|${layer.id}`;
            const celPixels = cels[key];
            if (celPixels) {
              this.collabSvc.broadcastCel(frameId, layer.id, celPixels, fw, fh);
            } else {
              // Cel was cleared or was never drawn — purge any stale yCels entry
              // so peers' recomposites don't include phantom content.
              this.collabSvc.broadcastCelClear(frameId, layer.id);
            }
          }
        }
      })
    ),
    { dispatch: false }
  );

  // ── Frame delete broadcast (no debounce) ────────────────────────────────────
  collabBroadcastDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FrameActions.deleteFrame),
      filter(() => this.collabSvc.canBroadcastCanvasSync()),
      tap((action) => {
        const deletedId = (action as any).frameId as string;
        if (deletedId) this.collabSvc.broadcastFrameDelete(deletedId);
      })
    ),
    { dispatch: false }
  );

  // ── Frame structure broadcast (debounced 50ms) ───────────────────────────────
  collabFrameStructure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FrameActions.addBlankFrame,
        FrameActions.duplicateFrame,
        FrameActions.deleteFrame,
        FrameActions.moveFrame,
        FrameActions.appendFrames,
        FrameActions.tweenFrames,
      ),
      filter(() => this.collabSvc.canBroadcastCanvasSync()),
      debounceTime(50),
      withLatestFrom(
        this.store.select(selectAllFrames),
        this.store.select(selectSettings),
        this.store.select(selectLayers),
        this.store.select(selectCels),
      ),
      tap(([action, allFrames, settings, layers, cels]) => {
        const w = settings.Resolution.width, h = settings.Resolution.height;
        const framesData = allFrames.map(f => ({
          id: f.id, pixels: f.pixels,
          width:  f.pixels[0]?.length ?? w,
          height: f.pixels.length    ?? h,
        }));
        this.collabSvc.broadcastAllFramesAtomic(framesData);

        // For frame duplication: the new frame's layer cels are created locally in
        // the reducer but never written to yCels.  Without this, peers have no cel
        // data for the new frame.  When anyone later draws on it, syncRemoteCels
        // recompositees from incomplete data and all other layers disappear.
        if (action.type === FrameActions.duplicateFrame.type) {
          const srcId = (action as any).frameId as string | undefined;
          if (srcId) {
            const srcIdx = allFrames.findIndex(f => f.id === srcId);
            // New frame is always inserted immediately after the source frame
            const newFrame = srcIdx !== -1 ? allFrames[srcIdx + 1] : null;
            if (newFrame) {
              const fw = newFrame.pixels[0]?.length ?? w;
              const fh = newFrame.pixels.length    ?? h;
              for (const layer of layers) {
                const key = `${newFrame.id}|${layer.id}`;
                const celPixels = cels[key];
                if (celPixels) {
                  this.collabSvc.broadcastCel(newFrame.id, layer.id, celPixels, fw, fh);
                }
              }
            }
          }
        }
      })
    ),
    { dispatch: false }
  );

  // ── Collab undo/redo cel broadcast (immediate) ───────────────────────────────
  // After undoCollab/redoCollab, broadcast the full cel state so all peers
  // recomposite to the correct post-undo state.  This handles two scenarios:
  //   1. Normal stroke undo: active layer's cels revert → broadcast those cels.
  //   2. Layer structural undo (e.g. undo of deleteLayer): the reducer now also
  //      restores the layers array and ALL cels from the snapshot.  We broadcast
  //      all cels so peers receive the restored layer's pixels (which were deleted
  //      from yCels by collabBroadcastDeleteLayer$ when the layer was first deleted).
  //      collabLayerStructure$ (which now includes undoCollab/redoCollab) broadcasts
  //      the layer list so peers' layer panels update too.
  collabBroadcastCollabUndoRedo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FrameActions.undoCollab, FrameActions.redoCollab),
      filter(() => this.collabSvc.canBroadcastCanvasSync() && this.collabSvc.can('draw')),
      withLatestFrom(
        this.store.select(selectAllFrames),
        this.store.select(selectCels),
        this.store.select(selectActiveLayerId),
        this.store.select(selectSettings),
        this.store.select(selectLayers),
      ),
      tap(([_, allFrames, cels, activeLayerId, settings, layers]) => {
        const w = settings.Resolution.width, h = settings.Resolution.height;
        // Always broadcast the full cel state so peers can recomposite correctly.
        // broadcastAllCels handles both updated and cleared (removed) cels.
        this.collabSvc.broadcastAllCels(cels, w, h);
        // Broadcast the layer list in case a layer was added/deleted by this undo.
        // collabLayerStructure$ (debounced 100ms) also handles this, but broadcasting
        // it immediately here ensures layers + cels arrive together.
        this.collabSvc.broadcastLayers(layers, activeLayerId);
      })
    ),
    { dispatch: false }
  );

  // ── Layer structure broadcast (debounced 100ms) ──────────────────────────────
  // Fires when layers are added, deleted, renamed, reordered, or have properties changed.
  // Sends the full layer list + activeLayerId so all peers stay in sync.
  collabLayerStructure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        FrameActions.addLayer,
        FrameActions.deleteLayer,
        FrameActions.renameLayer,
        FrameActions.moveLayerUp,
        FrameActions.moveLayerDown,
        // toggleLayerVisible and toggleLayerLocked are intentionally excluded:
        // visibility and lock are per-user local preferences, not room-wide state.
        FrameActions.toggleLayerContinuous,
        FrameActions.setLayerOpacity,
        FrameActions.setLayerBlend,
        FrameActions.setLayerTint,
        FrameActions.setLayerHold,
        FrameActions.mergeLayersDown,
      ),
      filter(() => this.collabSvc.canBroadcastCanvasSync()),
      debounceTime(100),
      withLatestFrom(this.store.select(selectLayers), this.store.select(selectActiveLayerId)),
      tap(([_action, layers, activeLayerId]) => {
        this.collabSvc.broadcastLayers(layers, activeLayerId);
      })
    ),
    { dispatch: false }
  );

  // ── Merge layer cel broadcast (immediate) ─────────────────────────────────────
  // mergeLayersDown modifies cels (merges upper into lower, removes upper) but
  // collabLayerStructure$ only broadcasts the new layer list.  Without this effect,
  // peers recomposite the current frame using the OLD lower-layer cel (pre-merge),
  // and the upper layer's stale cel remains in yCels for all frames.
  collabBroadcastMergeLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FrameActions.mergeLayersDown),
      filter(() => this.collabSvc.canBroadcastCanvasSync() && this.collabSvc.can('draw')),
      withLatestFrom(
        this.store.select(selectAllFrames),
        this.store.select(selectCurrentFrameId),
        this.store.select(selectCels),
        this.store.select(selectLayers),
        this.store.select(selectSettings),
      ),
      tap(([action, allFrames, currentFrameId, cels, layers, settings]) => {
        const upperLayerId = (action as any).layerId as string | undefined;
        const w = settings.Resolution.width, h = settings.Resolution.height;
        if (currentFrameId) {
          const frame = allFrames.find(f => f.id === currentFrameId);
          if (frame) {
            const fw = frame.pixels[0]?.length ?? w, fh = frame.pixels.length ?? h;
            // Broadcast the merged composite so peers see the result immediately
            this.collabSvc.broadcastFrame(currentFrameId, frame.pixels, fw, fh);
            // Broadcast all remaining layers' cels for the current frame so peers
            // can recomposite correctly (lower layer now has merged content)
            for (const layer of layers) {
              const key = `${currentFrameId}|${layer.id}`;
              const celPixels = cels[key];
              if (celPixels) {
                this.collabSvc.broadcastCel(currentFrameId, layer.id, celPixels, fw, fh);
              }
            }
          }
        }
        // Purge the upper (merged-away) layer's cel from yCels for ALL frames.
        // The upper layer no longer exists in the layer list; leaving its cels in
        // yCels is harmless visually (recomposite ignores unknown layer IDs) but
        // wastes space and can confuse peers who join after the merge.
        if (upperLayerId) {
          for (const f of allFrames) {
            this.collabSvc.broadcastCelClear(f.id, upperLayerId);
          }
        }
      })
    ),
    { dispatch: false }
  );

  // ── Delete layer cel cleanup (immediate) ─────────────────────────────────────
  // When a layer is deleted, its cels are removed from the local store but remain
  // in yCels (stale).  They don't affect recomposite visually (the layer is gone
  // from the layer list) but waste bandwidth during _resendAllCels and can
  // accumulate across sessions.  Clean them up so yCels stays lean.
  collabBroadcastDeleteLayer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FrameActions.deleteLayer),
      filter(() => this.collabSvc.canBroadcastCanvasSync()),
      withLatestFrom(this.store.select(selectAllFrames)),
      tap(([action, allFrames]) => {
        const layerId = (action as any).layerId as string | undefined;
        if (!layerId) return;
        for (const f of allFrames) {
          this.collabSvc.broadcastCelClear(f.id, layerId);
        }
      })
    ),
    { dispatch: false }
  );
}
